import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';

import { DataSource } from '@/types';
import { ProductRelocations } from '@/dto';
import { Icon, ProductFilter } from '@/core/components/controls';
import {
    TableConfig,
    TableComponent,
    TableComponentProps,
    TableColumn,
    columnTemplates,
    defaultTableConfig
} from '@/core/components/table';
import { localize } from '@/core/filters';

@Component
export default class ProductRelocationsTable extends Vue {
    @Prop({ type: String, required: true }) private tableKey!: string;
    @Prop({ type: Object, required: true }) private dataSource!: DataSource;

    public render(createElement: Vue.CreateElement): Vue.VNode {
        const { $listeners, tableKey, dataSource, columns, tableConfig } = this;

        for (const column of columns) {
            if (!column.allowEditing) {
                column.allowEditing = false;
            }
        }

        return createElement(TableComponent, {
            props: {
                tableKey,
                dataSource,
                columns,
                tableConfig
            } as TableComponentProps,
            on: {
                ...$listeners,
            },
        });
    }

    public get productRelocationComments() {
        const comments = localize('ProductRelocationComments');
        return Array.isArray(comments) ? comments : [];
    }

    private get columns(): TableColumn[] {
        return [
            {
                dataField: 'archive',
                caption: 'GridColumnTitleArchive',
                ...columnTemplates.COMPONENT<ProductRelocations>(
                    (item) => {
                        if (item.archive) {
                            return new Icon({
                                propsData: {
                                    name: 'nonActive'
                                }
                            });
                        }
                    })
            }, {
                dataField: 'accepted',
                caption: 'GridColumnTitleAccepted',
                ...columnTemplates.COMPONENT<ProductRelocations>(
                    (item) => {
                        if (item.accepted === true) {
                            return new Icon({
                                propsData: {
                                    name: 'checkmark'
                                }
                            });
                        }
                        if (item.accepted === false) {
                            return new Icon({
                                propsData: {
                                    name: 'blocked'
                                }
                            });
                        }
                    })
            }, {
                dataField: 'acceptor',
                caption: 'GridColumnTitleAcceptor',
            }, {
                dataField: 'source.name',
                caption: 'GridColumnTitleSource',
            }, {
                dataField: 'destination.name',
                caption: 'GridColumnTitleDestination',
            }, {
                dataField: 'loadingStartTime',
                caption: 'GridColumnTitleLoadingStartTime',
                ...columnTemplates.DATE_WITH_TIME(),
            }, {
                dataField: 'startTime',
                caption: 'GridColumnTitleStartTime',
                ...columnTemplates.DATE_WITH_TIME(),
            }, {
                dataField: 'finishTime',
                caption: 'GridColumnTitleFinishTime',
                ...columnTemplates.DATE_WITH_TIME(),
            }, {
                dataField: 'vehicleName',
                caption: 'GridColumnTitleRelocationName',
            }, {
                dataField: 'vehicleType',
                caption: 'GridColumnTitleVehicleType',
            }, {
                dataField: 'vehicleMaxWeight',
                caption: 'GridColumnTitleVehicleWeight',
                ...columnTemplates.WEIGHT(),
            }, {
                dataField: 'vehicleMaxWeightTonns',
                calculateCellValue: (rowData) => {
                    return rowData.vehicleMaxWeight / 1000;
                },
                caption: 'GridColumnTitleVehicleWeightTonns',
                ...columnTemplates.WEIGHT(),
            }, {
                dataField: 'vehicleVolume',
                caption: 'GridColumnTitleAvailableVolume',
                ...columnTemplates.VOLUME(),
            }, {
                dataField: 'vehicleMaxArea',
                caption: 'GridColumnTitleAvailableArea',
                ...columnTemplates.AREA(),
            }, {
                dataField: 'count',
                caption: 'GridColumnTitleQuantity',
                ...columnTemplates.NUMBER(),
            }, {
                dataField: 'volume',
                caption: 'GridColumnTitleQuantityVolume',
                ...columnTemplates.VOLUME(),
            }, {
                dataField: 'weight',
                caption: 'GridColumnTitleQuantityWeight',
                ...columnTemplates.WEIGHT(),
            }, {
                dataField: 'weightTonns',
                calculateCellValue: (rowData) => {
                    return rowData.weight / 1000;
                },
                caption: 'GridColumnTitleQuantityWeightTonns',
                ...columnTemplates.WEIGHT(),
            }, {
                dataField: 'packArea',
                caption: 'GridColumnTitlePackArea',
                ...columnTemplates.AREA(),
            }, {
                dataField: 'product.alias',
                caption: 'GridColumnTitleProductAlias',
            }, {
                dataField: 'product.name',
                caption: 'GridColumnTitleProductName',
                ...columnTemplates.COMPONENT<ProductRelocations>(
                    (rowData) => new ProductFilter({
                        propsData: {
                            product: rowData.product,
                        },
                    })
                ),
            }, {
                dataField: 'product.unit',
                caption: 'GridColumnTitleProductUnit',
            }, {
                dataField: 'productType.name',
                caption: 'GridColumnTitleProductType',
            }, {
                dataField: 'orderData',
                caption: 'GridColumnTitleOrderNumber',
            }, {
                dataField: 'storageCode',
                caption: 'GridColumnTitleStorageCode',
            }, {
                dataField: 'areaPlaceCode',
                caption: 'GridColumnTitleAreaPlaceCode',
            }, {
                dataField: 'stockData',
                caption: 'GridColumnTitleSourceExtData'
            }, {
                dataField: 'consignment',
                caption: 'GridColumnTitleConsignment'
            },
        ];
    }

    private get tableConfig(): Partial<TableConfig> {
        return {
            ...defaultTableConfig(this),
            onRowUpdated: (e: any) => {
                e.component.selectRows([e.key], true);
            },
            selection: {
                mode: 'multiple',
                showCheckBoxesMode: 'always'
            },
            editing: {
                mode: 'cell',
                refreshMode: 'repaint',
                allowUpdating: true
            },
            summary: {
                groupItems: [
                    {
                        column: 'vehicleType',
                        summaryType: 'min',
                        displayFormat: '{0}'
                    },
                    {
                        column: 'vehicleMaxWeight',
                        summaryType: 'min',
                        alignByColumn: true,
                        displayFormat: '{0}'
                    },
                    {
                        column: 'vehicleMaxWeightTonns',
                        summaryType: 'min',
                        alignByColumn: true,
                        displayFormat: '{0}'
                    },
                    {
                        column: 'vehicleVolume',
                        summaryType: 'min',
                        alignByColumn: true,
                        displayFormat: '{0}'
                    },
                    {
                        column: 'vehicleMaxArea',
                        summaryType: 'min',
                        alignByColumn: true,
                        displayFormat: '{0}'
                    },
                    {
                        column: 'weight',
                        summaryType: 'sum',
                        valueFormat: 'decimal',
                        alignByColumn: true,
                        displayFormat: '{0}'
                    },
                    {
                        column: 'weightTonns',
                        summaryType: 'sum',
                        alignByColumn: true,
                        displayFormat: '{0}'
                    },
                    {
                        column: 'volume',
                        summaryType: 'sum',
                        alignByColumn: true,
                        displayFormat: '{0}'
                    },
                    {
                        column: 'packArea',
                        summaryType: 'sum',
                        valueFormat: 'decimal',
                        alignByColumn: true,
                        displayFormat: '{0}'
                    },
                ]
            },
        };
    }
}
